import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Lock, Shield, AlertTriangle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { TwoFactorSetup } from '@/components/Admin/TwoFactorSetup';
import { TwoFactorVerify } from '@/components/Admin/TwoFactorVerify';
import { ForgotPasswordDialog } from '@/components/auth/ForgotPasswordDialog';

type LoginStep = 'login' | 'setup-2fa' | 'verify-2fa';

// Função para logar tentativa de login
const logLoginAttempt = async (data: {
  email: string;
  user_id?: string;
  success: boolean;
  failure_reason?: string;
}) => {
  try {
    await supabase.functions.invoke('log-admin-login-attempt', {
      body: data,
    });
  } catch (e) {
    console.error('Erro ao logar tentativa de login:', e);
  }
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>('login');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingFactorId, setPendingFactorId] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleAdminLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    try {
      // 1. Autenticar no Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        await logLoginAttempt({
          email,
          success: false,
          failure_reason: 'invalid_credentials',
        });
        setError('Credenciais inválidas. Verifique seu email e senha.');
        setIsSubmitting(false);
        return;
      }

      if (!authData.user) {
        await logLoginAttempt({
          email,
          success: false,
          failure_reason: 'no_user_returned',
        });
        setError('Erro ao autenticar. Tente novamente.');
        setIsSubmitting(false);
        return;
      }

      // 2. Verificar IMEDIATAMENTE se é Platform Admin
      const { data: isAdmin, error: rpcError } = await supabase.rpc('is_platform_admin', {
        check_user_id: authData.user.id,
      });

      if (rpcError) {
        console.error('Erro ao verificar admin:', rpcError);
        await logLoginAttempt({
          email,
          user_id: authData.user.id,
          success: false,
          failure_reason: 'rpc_error',
        });
        await supabase.auth.signOut();
        setError('Erro ao verificar permissões. Tente novamente.');
        setIsSubmitting(false);
        return;
      }

      // 3. Se NÃO for admin, fazer logout e mostrar erro
      if (!isAdmin) {
        await logLoginAttempt({
          email,
          user_id: authData.user.id,
          success: false,
          failure_reason: 'not_platform_admin',
        });
        await supabase.auth.signOut();
        setError('Acesso restrito à administração da plataforma.');
        setIsSubmitting(false);
        return;
      }

      // 4. Verificar status do 2FA
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();

      if (factorsError) {
        console.error('Erro ao verificar 2FA:', factorsError);
        // Continuar sem 2FA se houver erro na verificação
        await logLoginAttempt({
          email,
          user_id: authData.user.id,
          success: true,
        });
        toast.success('Bem-vindo ao Admin HQ!');
        navigate('/admin/dashboard');
        return;
      }

      const verifiedTotpFactors = factorsData?.totp?.filter(f => f.status === 'verified') || [];

      if (verifiedTotpFactors.length === 0) {
        // Precisa configurar 2FA pela primeira vez
        setStep('setup-2fa');
        setIsSubmitting(false);
      } else {
        // Já tem 2FA, precisa verificar
        setPendingFactorId(verifiedTotpFactors[0].id);
        setStep('verify-2fa');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Erro no login admin:', err);
      await logLoginAttempt({
        email,
        success: false,
        failure_reason: 'unexpected_error',
      });
      setError('Erro inesperado. Tente novamente.');
      setIsSubmitting(false);
    }
  };

  const handleTwoFactorComplete = async () => {
    // 2FA verificado com sucesso - logar e redirecionar
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await logLoginAttempt({
        email: user.email || '',
        user_id: user.id,
        success: true,
      });
    }
    toast.success('Bem-vindo ao Admin HQ!');
    navigate('/admin/dashboard');
  };

  const handleTwoFactorCancel = async () => {
    await supabase.auth.signOut();
    setStep('login');
    setPendingFactorId(null);
  };

  // Renderizar componente de setup 2FA
  if (step === 'setup-2fa') {
    return (
      <TwoFactorSetup
        onComplete={handleTwoFactorComplete}
        onCancel={handleTwoFactorCancel}
      />
    );
  }

  // Renderizar componente de verificação 2FA
  if (step === 'verify-2fa' && pendingFactorId) {
    return (
      <TwoFactorVerify
        factorId={pendingFactorId}
        onComplete={handleTwoFactorComplete}
        onCancel={handleTwoFactorCancel}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMjAiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTAtMTdjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTE3IDE3YzAtMi4yMDktMS43OTEtNC00LTRzLTQgMS43OTEtNCA0IDEuNzkxIDQgNCA0IDQtMS43OTEgNC00em0wLTE3YzAtMi4yMDktMS43OTEtNC00LTRzLTQgMS43OTEtNCA0IDEuNzkxIDQgNCA0IDQtMS43OTEgNC00em0tMzQgMTdjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTAtMTdjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
      
      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="p-4 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30">
              <Shield className="h-12 w-12 text-amber-500" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Acesso Administrativo</h1>
          <p className="text-slate-400 text-sm">Painel de Controle GOnX</p>
        </div>

        {/* Auth Card */}
        <Card className="shadow-2xl border-slate-800 bg-slate-900/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-lg text-white">Login Restrito</CardTitle>
            <CardDescription className="text-slate-400">
              Área exclusiva para administradores da plataforma
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4 bg-red-950/50 border-red-900">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email" className="text-slate-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                  <Input
                    id="admin-email"
                    name="email"
                    type="email"
                    placeholder="admin@gonx.com.br"
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500/50 focus:ring-amber-500/20"
                    required
                    autoComplete="email"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-password" className="text-slate-300">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                  <Input
                    id="admin-password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500/50 focus:ring-amber-500/20"
                    required
                    autoComplete="current-password"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-amber-500 hover:text-amber-400 hover:underline transition-colors"
                    disabled={isSubmitting}
                  >
                    Esqueci minha senha
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-medium"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  <>
                    <Shield className="mr-2 h-4 w-4" />
                    Acessar Painel
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-slate-500">
          <p>© 2024 GOnX - Plataforma Administrativa</p>
        </div>

        <ForgotPasswordDialog 
          open={showForgotPassword} 
          onOpenChange={setShowForgotPassword} 
        />
      </div>
    </div>
  );
}
